import { InputGroup } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { PlanrCheckbox } from "modules/common/components/planr/checkbox/PlanrCheckbox";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { ProjectFilter } from "modules/order-questions/components/BoardFilters";
import { OrderDictionaryType } from "modules/orders-manage/models/order-dictionary";
import { useEffect, useRef, useState } from "react";
import { AgentMailStoreType, MODE_ALL, MODE_NO_ORDER, MODE_UNASSIGNED } from "../models/agent-mail-store";
import { Avatar } from "./Avatar";
import { formatMailDate } from "./mail-format";
import styles from "./AgentMailbox.module.scss";

const MODES = [
    { id: MODE_ALL, label: "Все" },
    { id: MODE_NO_ORDER, label: "Без договора" },
    { id: MODE_UNASSIGNED, label: "Неразобранные" },
];

interface ThreadListProps {
    store: AgentMailStoreType;
    /** Переписка одного договора: режимы и договор в строке не нужны */
    orderFixed: boolean;
    orderDictionary: OrderDictionaryType;
    onNew: () => void;
    onOpen: (key: string) => void;
}

export const ThreadList = observer(({ store, orderFixed, orderDictionary, onNew, onOpen }: ThreadListProps) => {
    const [search, setSearch] = useState(store.filter.search);
    const [syncing, setSyncing] = useState(false);

    const applySearch = (value: string) => {
        if (value.trim() === store.filter.search.trim()) {
            return;
        }

        store.setFilter({ search: value });
        store.loadThreads();
    };

    // ищем по мере ввода: запрос уходит, когда пауза в наборе дольше 400 мс
    const typing = useRef<number | undefined>(undefined);
    useEffect(() => {
        window.clearTimeout(typing.current);
        typing.current = window.setTimeout(() => applySearch(search), 400);
        return () => window.clearTimeout(typing.current);
        // applySearch читает стор напрямую, перезапускать таймер нужно только по тексту
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const sync = async () => {
        setSyncing(true);
        await store.sync();
        setSyncing(false);
    };

    return (
        <div className={styles.sidebar}>
            <div className={styles.sidebarHead}>
                <div className={styles.titleRow}>
                    <div className={styles.title}>
                        Переписка
                        {store.unread > 0 && (
                            <span className={styles.counter} title="Переписок с новыми ответами">
                                {store.unread}
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        className={styles.iconButton}
                        onClick={sync}
                        disabled={syncing}
                        title="Проверить почту сейчас (по расписанию — раз в 5 минут)"
                    >
                        <span className={syncing ? styles.spin : ""} style={{ display: "inline-flex" }}>
                            <GeneralIcon type="general-refresh" />
                        </span>
                    </button>
                    <PlanrButton type="greenish" size="small" icon="general-plus" onClick={onNew}>
                        Написать
                    </PlanrButton>
                </div>

                <div className={styles.search}>
                    <span className={styles.searchIcon}>
                        <GeneralIcon type="general-search" size={14} />
                    </span>
                    <InputGroup
                        className="planr-default-input"
                        placeholder="Тема, текст или адрес"
                        value={search}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.currentTarget.value)}
                        onKeyDown={(e: React.KeyboardEvent) => e.key === "Enter" && applySearch(search)}
                        rightElement={
                            search ? (
                                <button
                                    type="button"
                                    className={styles.iconButton}
                                    title="Очистить"
                                    onClick={() => {
                                        setSearch("");
                                        applySearch("");
                                    }}
                                >
                                    <GeneralIcon type="general-cross-small" size={12} />
                                </button>
                            ) : undefined
                        }
                    />
                </div>

                {!orderFixed && (
                    <div className={styles.objects}>
                        <ProjectFilter
                            projects={orderDictionary}
                            selected={store.filter.orderIds}
                            onChange={(ids) => {
                                // с выбранными объектами «Без договора» и «Неразобранные» пусты
                                store.setFilter(ids.length > 0 ? { orderIds: ids, mode: MODE_ALL } : { orderIds: ids });
                                store.loadThreads();
                            }}
                        />
                    </div>
                )}

                {!orderFixed && (
                    <div className={styles.segmented}>
                        {MODES.map((mode) => (
                            <button
                                key={mode.id}
                                type="button"
                                className={`${styles.segment} ${store.filter.mode === mode.id ? styles.segmentActive : ""}`}
                                onClick={() => {
                                    // письма без договора к выбранным объектам не относятся
                                    store.setFilter(
                                        mode.id === MODE_ALL ? { mode: mode.id } : { mode: mode.id, orderIds: [] }
                                    );
                                    store.loadThreads();
                                }}
                            >
                                {mode.label}
                            </button>
                        ))}
                    </div>
                )}

                <div className={styles.unreadToggle}>
                    <PlanrCheckbox
                        checked={store.filter.unreadOnly}
                        onChange={(checked) => {
                            store.setFilter({ unreadOnly: checked });
                            store.loadThreads();
                        }}
                    >
                        Только с новыми ответами
                    </PlanrCheckbox>
                    <span className={styles.replyHint}>{store.total > 0 ? `всего ${store.total}` : ""}</span>
                </div>
            </div>

            <div className={styles.threads}>
                {store.loadingThreads && <LocalLoader active />}

                {store.threads.length === 0 && !store.loadingThreads && (
                    <div className={styles.empty}>
                        <span className={styles.emptyIcon}>
                            <GeneralIcon type="general-mail" size={24} />
                        </span>
                        <div className={styles.emptyTitle}>Писем нет</div>
                        <div>Измените фильтр или напишите агентам первыми</div>
                    </div>
                )}

                {store.threads.map((thread) => {
                    const who = thread.unassigned
                        ? thread.senderName || thread.senderEmail || "Неизвестный отправитель"
                        : thread.agents.map((a) => a.name).join(", ") || "Без получателей";
                    // у переписки с несколькими агентами аватар - первого из них
                    const face = thread.unassigned ? who : thread.agents[0]?.name || who;

                    return (
                        <div
                            key={thread.key}
                            className={[
                                styles.thread,
                                thread.key === store.current ? styles.threadActive : "",
                                thread.unread > 0 ? styles.threadUnread : "",
                            ].join(" ")}
                            onClick={() => onOpen(thread.key)}
                        >
                            <Avatar name={face} />
                            <div style={{ minWidth: 0 }}>
                                <div className={styles.threadTop}>
                                    <span className={styles.threadWho}>{who}</span>
                                    <span className={styles.threadDate}>
                                        {formatMailDate(thread.lastAt, thread.lastTime)}
                                    </span>
                                </div>
                                <div className={styles.threadSubject}>{thread.subject || "(без темы)"}</div>
                                {thread.preview && (
                                    <div className={styles.threadPreview}>
                                        {thread.lastAuthor && !thread.lastIncoming ? `${thread.lastAuthor}: ` : ""}
                                        {thread.preview}
                                    </div>
                                )}
                                <div className={styles.threadMeta}>
                                    {thread.unassigned && (
                                        <span className={`${styles.chip} ${styles.chipWarning}`}>не привязано</span>
                                    )}
                                    {!orderFixed && thread.order && (
                                        <span
                                            className={`${styles.chip} ${styles.chipOrder}`}
                                            title={thread.order.name}
                                        >
                                            {thread.order.name}
                                        </span>
                                    )}
                                    {thread.failed > 0 && (
                                        <span
                                            className={`${styles.chip} ${styles.chipDanger}`}
                                            title="Есть недоставленные письма"
                                        >
                                            не доставлено
                                        </span>
                                    )}
                                    <span className={styles.spacer} />
                                    {thread.count > 1 && (
                                        <span
                                            className={`${styles.chip} ${styles.chipMuted}`}
                                            title="Писем в переписке"
                                        >
                                            {thread.count}
                                        </span>
                                    )}
                                    {thread.unread > 0 && (
                                        <span className={styles.badge} title="Новых ответов">
                                            {thread.unread}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}

                {store.hasMore && (
                    <div className={styles.more}>
                        <PlanrButton type="lightblueish" size="small" onClick={() => store.loadThreads(true)}>
                            Показать ещё
                        </PlanrButton>
                    </div>
                )}
            </div>
        </div>
    );
});
